import moment from "moment";

const safeStr = (v) => (v == null ? "" : String(v));

const safeNum = (v) => {
  const n = Number(v);
  return Number.isNaN(n) ? 0 : n;
};

const safeArr = (v) => (Array.isArray(v) ? v : []);

const repeat = (char, n) => new Array(Math.max(0, n) + 1).join(char);

const left = (value, width) => safeStr(value).slice(0, width).padEnd(width, " ");

const right = (value, width) => safeStr(value).slice(0, width).padStart(width, " ");

const center = (value, width) => {
  const text = safeStr(value);
  if (!text) return "";
  if (text.length >= width) return text;

  const totalSpaces = width - text.length;
  const leftSpaces = Math.floor(totalSpaces / 2);
  const rightSpaces = totalSpaces - leftSpaces;

  return `${repeat(" ", leftSpaces)}${text}${repeat(" ", rightSpaces)}`;
};

export const PRINTER_PROFILES = {
  EPSON_80: {
    paperWidth: 80,
    columns: 48,
    nameWidth: 20,
    qtyWidth: 4,
    rateWidth: 10,
    amtWidth: 12,
    feedBeforeCutLines: 8,
  },
  EPSON_58: {
    paperWidth: 58,
    columns: 32,
    nameWidth: 12,
    qtyWidth: 4,
    rateWidth: 8,
    amtWidth: 8,
    feedBeforeCutLines: 8,
  },
};

const DEFAULT_PROFILE = PRINTER_PROFILES.EPSON_80;

export const wrapNoWordBreak = (text, width) => {
  const clean = safeStr(text).replace(/\s+/g, " ").trim();
  if (!clean) return [""];

  const words = clean.split(" ");
  const lines = [];
  let current = "";

  for (const word of words) {
    if (word.length > width) {
      if (current) {
        lines.push(current);
        current = "";
      }

      let remaining = word;
      while (remaining.length > width) {
        lines.push(remaining.slice(0, width));
        remaining = remaining.slice(width);
      }
      if (remaining) lines.push(remaining);
      continue;
    }

    const next = current ? `${current} ${word}` : word;

    if (next.length <= width) {
      current = next;
    } else {
      if (current) lines.push(current);
      current = word;
    }
  }

  if (current) lines.push(current);

  return lines;
};

export const normalizeReceiptData = (data = {}) => {
  const items = safeArr(data.items)
    .filter((it) => safeStr(it?.productName || it?.product).trim() && safeNum(it?.quantity) > 0)
    .map((it) => {
      const name = safeStr(it?.productName || it?.product).trim();
      const qty = safeNum(it?.quantity);
      const rate =
        it?.unitPrice != null ? safeNum(it.unitPrice) : safeNum(it?.rate);

      const amount =
        it?.price != null
          ? safeNum(it.price)
          : it?.amount != null
            ? safeNum(it.amount)
            : qty * rate;

      return {
        name,
        qty,
        rate,
        amount,
      };
    });

  const totalQty = items.reduce((sum, it) => sum + safeNum(it.qty), 0);
  const discount = safeNum(data.discount);
  const subTotal = items.reduce((sum, it) => sum + safeNum(it.amount), 0);
  const totalAmount =
    data?.totalAmount != null ? safeNum(data.totalAmount) : Math.max(0, subTotal - discount);

  const rawDate = data?.salesDate || data?.date || new Date();
  const dateText = moment(rawDate).isValid() ? moment(rawDate).format("DD-MM-YYYY") : "";

  return {
    shopName: safeStr(data?.shopName || "NEW VINAYAGA SNACKS"),
    shopTagline: safeStr(data?.shopTagline || "& CONFECTIONERIES"),
    address1: safeStr(data?.address1 || "No:3/132 Pillaiyar Koil Street"),
    address2: safeStr(data?.address2 || "Kelambakkam - 603103"),
    mobile: safeStr(data?.mobile || ""),
    customerName: safeStr(data?.customerName || "Walk-in Customer"),
    contactNumber: safeStr(data?.contactNumber || ""),
    mode: safeStr(data?.mode || data?.transactionMode || "Cash"),
    invoiceNo: safeStr(data?.invoiceNo || ""),
    footer1: safeStr(data?.footer1 || "Goods Once Sold cannot be taken back"),
    footer2: safeStr(data?.footer2 || "Thank You !! Visit Again !!"),
    discount,
    totalAmount,
    totalQty,
    dateText,
    items,
  };
};

const buildTwoColumnLine = (leftText, rightText, columns) => {
  const L = safeStr(leftText);
  const R = safeStr(rightText);

  if (!L && !R) return "";

  if (L.length + R.length + 1 <= columns) {
    return L + repeat(" ", columns - L.length - R.length) + R;
  }

  const half = Math.floor(columns / 2);
  return left(L, half) + right(R, columns - half);
};

const buildItemLines = (item, profile) => {
  const { nameWidth, qtyWidth, rateWidth, amtWidth } = profile;
  const wrappedName = wrapNoWordBreak(item.name, nameWidth);

  return wrappedName.map((nameLine, index) => {
    if (index === 0) {
      return (
        left(nameLine, nameWidth) +
        right(item.qty, qtyWidth) +
        right(item.rate.toFixed(2), rateWidth) +
        right(item.amount.toFixed(2), amtWidth)
      );
    }

    return (
      left(nameLine, nameWidth) +
      right("", qtyWidth) +
      right("", rateWidth) +
      right("", amtWidth)
    );
  });
};

export const buildReceiptLines = (data = {}, profile = DEFAULT_PROFILE, options = {}) => {
  const d = normalizeReceiptData(data);
  const columns = profile.columns;
  const divider = repeat("-", columns);
  const lines = [];
  const footerPaddingLines = safeNum(options.footerPaddingLines || 5);

  lines.push(center(d.shopName, columns));
  if (d.shopTagline) lines.push(center(d.shopTagline, columns));
  lines.push(divider);

  if (d.address1) lines.push(center(d.address1, columns));
  if (d.address2) lines.push(center(d.address2, columns));
  if (d.mobile) lines.push(center(`Mobile : ${d.mobile}`, columns));

  lines.push(divider);

  lines.push(buildTwoColumnLine(`Cust : ${d.customerName}`, `Date : ${d.dateText}`, columns));
  lines.push(buildTwoColumnLine(`Mob  : ${d.contactNumber}`, `Mode : ${d.mode}`, columns));
  lines.push(buildTwoColumnLine("", `Inv  : #${d.invoiceNo}`, columns));

  lines.push(divider);

  lines.push(
    left("Item", profile.nameWidth) +
    right("Qty", profile.qtyWidth) +
    right("Rate", profile.rateWidth) +
    right("Amt", profile.amtWidth)
  );

  lines.push(divider);

  d.items.forEach((item) => {
    buildItemLines(item, profile).forEach((line) => lines.push(line));
  });

  lines.push(divider);
  lines.push(buildTwoColumnLine("Total Items", `${d.totalQty}`, columns));

  if (d.discount > 0) {
    lines.push(buildTwoColumnLine("Discount", `-Rs.${d.discount.toFixed(2)}`, columns));
  }

  lines.push(buildTwoColumnLine("TOTAL", `Rs.${d.totalAmount.toFixed(2)}`, columns));
  lines.push(divider);

  if (d.footer1) lines.push(center(d.footer1, columns));
  if (d.footer2) lines.push(center(d.footer2, columns));

  for (let i = 0; i < footerPaddingLines; i += 1) {
    lines.push("");
  }

  return lines;
};

const ESC = 0x1b;
const GS = 0x1d;

const concatUint8Arrays = (...arrays) => {
  const totalLength = arrays.reduce((sum, arr) => sum + arr.length, 0);
  const result = new Uint8Array(totalLength);
  let offset = 0;

  arrays.forEach((arr) => {
    result.set(arr, offset);
    offset += arr.length;
  });

  return result;
};

export const buildEscPosBytes = (data = {}, profile = DEFAULT_PROFILE, options = {}) => {
  try {
    const {
      cutPaper = false,
      footerPaddingLines = 5,
      feedBeforeCutLines = profile.feedBeforeCutLines || 8,
    } = options;

    const d = normalizeReceiptData(data);
    const lines = buildReceiptLines(data, profile, { footerPaddingLines });
    const encoder = new TextEncoder();

    const initialize = new Uint8Array([ESC, 0x40]);

    const alignCenter = new Uint8Array([ESC, 0x61, 0x01]);
    const alignLeft = new Uint8Array([ESC, 0x61, 0x00]);

    const lineSpacingDefault = new Uint8Array([ESC, 0x32]);

    const fontA = new Uint8Array([ESC, 0x4d, 0x00]); // default

    const boldOn = new Uint8Array([ESC, 0x45, 0x01]);
    const boldOff = new Uint8Array([ESC, 0x45, 0x00]);

    const doubleSize = new Uint8Array([GS, 0x21, 0x11]); // double width + double height for large shop header
    const normalSize = new Uint8Array([GS, 0x21, 0x00]);

    const headerLines = [
      d.shopName,
      ...(d.shopTagline ? [d.shopTagline] : []),
    ];

    const restLines = lines.slice(headerLines.length);

    const headerText = encoder.encode(`${headerLines.join("\n")}\n`);
    const restText = encoder.encode(`${restLines.join("\n")}\n`);

    const body = concatUint8Arrays(
      alignCenter,
      fontA,
      doubleSize,
      boldOn,
      headerText,
      boldOff,
      normalSize,
      fontA,
      alignLeft,
      restText
    );

    const feed = new Uint8Array([
      ESC,
      0x64,
      Math.min(12, Math.max(0, feedBeforeCutLines)),
    ]);

    const cut = new Uint8Array([GS, 0x56, 0x42, 0x00]);

    return concatUint8Arrays(
      initialize,
      lineSpacingDefault,
      body,
      feed,
      cutPaper ? cut : new Uint8Array([])
    );
  } catch (error) {
    throw new Error(`ESC/POS build failed: ${error?.message || "Unknown error"}`);
  }
};

export const bytesToBase64 = (bytes) => {
  try {
    const arr = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
    let binary = "";
    const chunkSize = 0x8000;

    for (let i = 0; i < arr.length; i += chunkSize) {
      const chunk = arr.subarray(i, i + chunkSize);
      binary += String.fromCharCode(...chunk);
    }

    return btoa(binary);
  } catch (error) {
    throw new Error(`Base64 conversion failed: ${error?.message || "Unknown error"}`);
  }
};

export const buildRawBtUrl = (bytes) => {
  if (!bytes || !bytes.length) {
    throw new Error("No printer bytes generated.");
  }

  const encoded = bytesToBase64(bytes);

  if (!encoded) {
    throw new Error("Failed to encode printer bytes.");
  }

  return `rawbt:base64,${encoded}`;
};

export const printViaRawBTBytes = (bytes) => {
  try {
    if (typeof window === "undefined") {
      throw new Error("Window is not available.");
    }

    const rawbtUrl = buildRawBtUrl(bytes);

    setTimeout(() => {
      window.location.href = rawbtUrl;
    }, 50);

    return true;
  } catch (error) {
    throw new Error(`RawBT handoff failed: ${error?.message || "Unknown error"}`);
  }
};

export const handlePosPrint = async (
  orderFormData,
  onSuccess = null,
  options = {}
) => {
  const {
    profile = PRINTER_PROFILES.EPSON_80,
    transport = "rawbt",
    cutPaper = false,
    footerPaddingLines = 5,
    feedBeforeCutLines = profile.feedBeforeCutLines || 8,
  } = options;

  if (!orderFormData) {
    throw new Error("orderFormData is required.");
  }

  if (transport !== "rawbt") {
    throw new Error("Only RawBT transport is supported.");
  }

  const bytes = buildEscPosBytes(orderFormData, profile, {
    cutPaper,
    footerPaddingLines,
    feedBeforeCutLines,
  });

  printViaRawBTBytes(bytes);

  if (typeof onSuccess === "function") {
    await onSuccess();
  }

  return true;
};

export const debugReceiptText = (orderFormData, profile = DEFAULT_PROFILE, options = {}) => {
  return buildReceiptLines(orderFormData, profile, options).join("\n");
};

export const debugReceiptBytes = (orderFormData, profile = DEFAULT_PROFILE, options = {}) => {
  return Array.from(buildEscPosBytes(orderFormData, profile, options));
};

export const debugRawBtUrl = (orderFormData, profile = DEFAULT_PROFILE, options = {}) => {
  const bytes = buildEscPosBytes(orderFormData, profile, options);
  return buildRawBtUrl(bytes);
};
